import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

import { DataTableItem, DataTableRow, Icon, InputSwitch } from "../Component";
import ConfirmationModal from "../modals/ConfirmationModal";

import partnerService from "../../services/partnerService";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import EventEmitter, { EVENT_TOPICS } from "../../utilities/eventUtils";
import {
  DATE_FORMATS,
  LANGUAGE_KEYS,
  PARTNER_CATEGORIES_READABLE,
} from "../../utilities/constants";
import { formatDate } from "../../utilities/dateTimeUtils";

const TOGGLE_QUESTIONS = {
  showInHomeSlider: {
    on: "Show this partner in the home slider?",
    off: "Remove this partner from the home slider?",
  },
  isActive: {
    on: "Activate this partner?",
    off: "Deactivate this partner?",
  },
};

const PartnersPageTableBodyItem = ({ item }) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [onFetch, isDeleteLoading] = useBeManualFetcher();
  const [onToggleFetch, isToggleLoading] = useBeManualFetcher();

  const [showInHomeSlider, setShowInHomeSlider] = useState(
    item?.showInHomeSlider
  );
  const [isActive, setIsActive] = useState(item?.isActive);

  const [pendingToggle, setPendingToggle] = useState(null);
  // InputSwitch uncontrolled: defaultChecked faqat mount paytida qo'llanadi.
  // Nonce o'zgarganda komponent qayta mount bo'lib, ko'rinish holatga moslashadi
  // (bekor qilinganda yoki so'rov xato bo'lganda kerak).
  const [switchNonce, setSwitchNonce] = useState(0);

  const resyncSwitches = () => setSwitchNonce((nonce) => nonce + 1);

  const handlePartnerDelete = async (id) => {
    await onFetch({
      action: () => partnerService.removePartner(id),
      onLoad: () => {
        EventEmitter.emit(EVENT_TOPICS.ON_PARTNER_DELETED, {
          deletedPartnerId: id,
        });

        setShowDeleteModal(false);
      },
      successMessage: "Partner successfully deleted",
    });
  };

  const handleToggleConfirm = () => {
    const { field, currentValue, nextValue, setValue, successMessage } =
      pendingToggle;

    setValue(nextValue);

    onToggleFetch({
      action: () =>
        partnerService.updatePartner(item?.id, {
          data: { [field]: nextValue },
        }),
      onLoad: (updatedPartner) => {
        EventEmitter.emit(EVENT_TOPICS.ON_PARTNER_UPDATED, { updatedPartner });
        setPendingToggle(null);
      },
      actionWhenError: () => {
        setValue(currentValue);
        resyncSwitches();
        setPendingToggle(null);
      },
      successMessage,
    });
  };

  const handleToggleCancel = () => {
    setPendingToggle(null);
    resyncSwitches();
  };

  const requestToggle = ({ field, currentValue, setValue, successMessage }) =>
    setPendingToggle({
      field,
      currentValue,
      nextValue: !currentValue,
      setValue,
      successMessage,
      question: TOGGLE_QUESTIONS[field][currentValue ? "off" : "on"],
    });

  const handleRowClick = (e) => {
    // Modal portal orqali chiziladi, lekin React eventi baribir qatorga ko'tariladi.
    if (!e.currentTarget.contains(e.target)) return;
    // Switch, dropdown va nom linki o'z ishini qiladi.
    if (e.target.closest("input, label, a, button, .dropdown")) return;

    navigate(`/partners/${item?.id}`, { state: item });
  };

  return (
    <DataTableItem style={{ cursor: "pointer" }} onClick={handleRowClick}>
      <DataTableRow noText>{item?.orderIndex ?? "-"}</DataTableRow>
      <DataTableRow noText>
        {item?.logo ? (
          <img
            src={item.logo}
            alt={item?.name?.[LANGUAGE_KEYS.EN] ?? "logo"}
            style={{ width: 56, height: 56, objectFit: "contain" }}
          />
        ) : (
          <span className="text-soft">-</span>
        )}
      </DataTableRow>
      <DataTableRow noText>
        <Link to={`/partners/${item?.id}`} state={item}>
          <span className="tb-lead">{item?.name?.[LANGUAGE_KEYS.EN]}</span>
        </Link>
      </DataTableRow>
      <DataTableRow
        text={
          PARTNER_CATEGORIES_READABLE[item?.category] ?? item?.category ?? "-"
        }
      />
      <DataTableRow
        size="sm"
        text={formatDate(item?.createdAt, DATE_FORMATS.DATE_REGISTERED)}
      />
      <DataTableRow noText>
        <InputSwitch
          key={`home-slider-${showInHomeSlider}-${switchNonce}`}
          id={`${item?.id}-home-slider`}
          checked={showInHomeSlider}
          onClick={() =>
            requestToggle({
              field: "showInHomeSlider",
              currentValue: showInHomeSlider,
              setValue: setShowInHomeSlider,
              successMessage: "Home slider updated",
            })
          }
        />
      </DataTableRow>
      <DataTableRow noText>
        <InputSwitch
          key={`active-${isActive}-${switchNonce}`}
          id={`${item?.id}-active`}
          checked={isActive}
          onClick={() =>
            requestToggle({
              field: "isActive",
              currentValue: isActive,
              setValue: setIsActive,
              successMessage: "Active status updated",
            })
          }
        />
      </DataTableRow>

      <DataTableRow className="nk-tb-col-tools" noText>
        <ul className="nk-tb-actions gx-1">
          <li>
            <UncontrolledDropdown>
              <DropdownToggle
                tag="a"
                className="dropdown-toggle btn btn-icon btn-trigger"
              >
                <Icon name="more-h"></Icon>
              </DropdownToggle>
              <DropdownMenu right>
                <ul className="link-list-opt no-bdr">
                  <li>
                    <DropdownItem
                      tag="a"
                      href="#delete"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDeleteModal(true);
                      }}
                    >
                      <Icon name="trash"></Icon>
                      <span>Delete</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          </li>
        </ul>
      </DataTableRow>

      <ConfirmationModal
        showModal={Boolean(pendingToggle)}
        onClose={handleToggleCancel}
        onConfirm={handleToggleConfirm}
        modalTitle={pendingToggle?.question}
        confirmTitle="Yes"
        isLoading={isToggleLoading}
      />
      <ConfirmationModal
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handlePartnerDelete(item?.id)}
        confirmColor="danger"
        confirmTitle="Delete"
        isLoading={isDeleteLoading}
      />
    </DataTableItem>
  );
};

export default PartnersPageTableBodyItem;
